<template>
  <themed-modal
    name="manage-sites-modal"
    title="Manage Project Sites"
    size="xl"
    :scrollable="true"
    :clickToClose="true"
    @opened="handleModalOpened"
    @closed="handleModalClosed"
  >
    <div class="manage-sites-modal">
      <!-- Use SiteCards component with checkboxes -->
      <site-cards
        :site-data="allSites"
        :show-search="true"
        :show-checkboxes="true"
        :selected-site-ids="projectSiteIds"
        :default-view-mode="'grid'"
        :default-page-size="10"
        :page-size-options="[10, 15, 20]"
        :default-show-archived="showArchivedSites"
        :disable-preferences="true"
        :show-pagination="true"
        :show-view-toggle="false"
        :show-new-site-button="false"
        @site-selection-changed="handleSiteSelectionChanged"
      />
    </div>
  </themed-modal>
</template>

<script>
import { mapState } from 'vuex';
import ThemedModal from '../ThemedModal.vue';
import SiteCards from '../sites/SiteCards.vue';

export default {
  name: 'ManageSitesModal',

  components: {
    ThemedModal,
    SiteCards
  },

  props: {
    projectId: {
      type: Number,
      required: true
    },
    projectSites: {
      type: Array,
      required: true,
      default: () => []
    },
    showArchivedSites: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isLoading: false
    };
  },

  computed: {
    ...mapState(['sites']),

    // Get all available sites from Vuex store
    allSites() {
      return this.sites.all || [];
    },

    // Get IDs of sites currently in the project
    projectSiteIds() {
      return this.projectSites.map(site => site.id);
    }
  },

  methods: {
    // Handle site selection change from SiteCards
    async handleSiteSelectionChanged({ site, selected }) {
      if (selected) {
        await this.addSite(site);
      } else {
        await this.removeSite(site);
      }
    },

    // Add site to project
    async addSite(site) {
      this.isLoading = true;

      try {
        const response = await axios.post(`/projects/${this.projectId}/sites`, {
          site_id: site.id
        });

        // Update parent component with new sites list
        this.$emit('sites-updated', response.data.sites);

        // Show success notification
        this.$notify({
          type: 'success',
          title: 'Success',
          text: response.data.message || 'Site Added'
        });
      } catch (error) {
        // Show error notification
        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to add site'
        });

        console.error('Error adding site:', error);
      } finally {
        this.isLoading = false;
      }
    },

    // Remove site from project
    async removeSite(site) {
      // Find the pivot ID
      const projectSite = this.projectSites.find(ps => ps.id === site.id);
      if (!projectSite || !projectSite.pivot) {
        console.error('Could not find pivot ID for site:', site.id);
        return;
      }

      const pivotId = projectSite.pivot.id;
      this.isLoading = true;

      try {
        const response = await axios.delete(`/projects/${this.projectId}/sites/${pivotId}`);

        // Update parent component with new sites list
        this.$emit('sites-updated', response.data.sites);

        // Show success notification
        this.$notify({
          type: 'success',
          title: 'Success',
          text: response.data.message || 'Site Removed'
        });
      } catch (error) {
        // Show error notification
        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to remove site'
        });

        console.error('Error removing site:', error);
      } finally {
        this.isLoading = false;
      }
    },

    // Handle modal opened event
    handleModalOpened() {
      // Reset state when modal opens if needed
    },

    // Handle modal closed event
    handleModalClosed() {
      // Clean up if needed
    }
  }
};
</script>
